import { SingleNews } from '@api';
import { useQueryParams } from '@terminal/hooks/use-query-params/useQueryParams';
import { useTrackEmailDrawerOpen } from '@terminal/hooks/use-track-email-drawer-open/useTrackEmailDrawerOpen';
import { queryKeys } from '@terminal/routes/query-params';
import {
  POSTHOG_TRACKING_SOURCES,
  PosthogTrackingSource,
} from '@terminal/services/tracking/posthog/constants';
import { usePosthogEvents } from '@terminal/services/tracking/posthog/use-posthog-events/usePosthogEvents';

interface OpenArticleDrawerParams {
  newsId: string;
  entities?: string[];
  queryParams?: Record<string, string | undefined>;
}

export const useArticleDrawer = (source: PosthogTrackingSource) => {
  const { params, deleteQueryParam, setQueryParams } = useQueryParams();
  const { trackNewsDrawerOpen } = usePosthogEvents();
  const activeArticleId = params[queryKeys.activeArticleId] ?? undefined;

  useTrackEmailDrawerOpen({
    isDrawerOpen:
      params[queryKeys.activeDrawer] === 'article' && !!activeArticleId,
    utmSource: params[queryKeys.utmSource],
    trackingKey: activeArticleId,
    onTrackEmailOpen: () => {
      if (!activeArticleId) {
        return;
      }

      trackNewsDrawerOpen({
        source: POSTHOG_TRACKING_SOURCES.EMAIL,
        newsId: activeArticleId,
      });
    },
  });

  const openArticleDrawer = ({
    newsId,
    entities,
    queryParams,
  }: OpenArticleDrawerParams) => {
    trackNewsDrawerOpen({
      source,
      newsId,
      entities,
    });

    setQueryParams({
      ...queryParams,
      [queryKeys.activeArticleId]: newsId,
      [queryKeys.activeDrawer]: 'article',
    });
  };

  const handleSelectSingleNews = (article: SingleNews) => {
    openArticleDrawer({
      newsId: article.id,
      entities: article.entities,
    });
  };

  const closeDrawer = () => {
    deleteQueryParam([queryKeys.activeArticleId, queryKeys.activeDrawer]);
  };

  return {
    openArticleDrawer,
    handleSelectSingleNews,
    closeDrawer,
    singleNewsId: activeArticleId,
  };
};
